import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

import { invoiceService, saleService } from "../../services";
import { printSaleTicket } from "../sale/SaleTicket";

const DEFAULT_SETTINGS = {
  invoices_enabled: false,
  tickets_enabled: true,
  deferred_billing_enabled: false,
};

const normalizeSettings = (settings = {}) => ({
  invoices_enabled: Boolean(settings?.invoices_enabled),
  tickets_enabled: settings?.tickets_enabled !== false,
  deferred_billing_enabled: Boolean(settings?.invoices_enabled && settings?.deferred_billing_enabled),
});

const normalizeSalesConfig = (config = {}) => ({
  allow_billing: config?.allow_billing === true,
  allow_deferred_billing: config?.allow_billing === true && config?.allow_deferred_billing === true,
});

const extractErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]) return String(data.detail[0]);
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

export default function usePostSaleInvoiceFlow({ showSnack } = {}) {
  const dialogResolveRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const askInvoicePrint = useCallback(
    () =>
      new Promise((resolve) => {
        dialogResolveRef.current = resolve;
        setDialogOpen(true);
      }),
    []
  );

  const resolveDialog = useCallback((value) => {
    const resolve = dialogResolveRef.current;
    dialogResolveRef.current = null;
    setDialogOpen(false);
    resolve?.(value);
  }, []);

  const handlePostSaleInvoiceFlow = useCallback(
    async ({ sale, branchName, cashierName, allowTicketPrint = true }) => {
      if (!sale?.id || String(sale?.status || "").toUpperCase() !== "COMPLETED") {
        return;
      }

      let settings = DEFAULT_SETTINGS;
      try {
        settings = normalizeSettings(await invoiceService.getSettings());
      } catch {
        showSnack?.("No se pudo cargar la configuracion FEL. Se imprimira ticket.", "warning");
      }

      let salesConfig = { allow_billing: false, allow_deferred_billing: false };
      try {
        salesConfig = normalizeSalesConfig(await saleService.getSalesConfig());
      } catch {
        salesConfig = { allow_billing: false, allow_deferred_billing: false };
      }
      if (!salesConfig.allow_billing) {
        settings = { ...settings, invoices_enabled: false, tickets_enabled: true, deferred_billing_enabled: false };
      } else if (!salesConfig.allow_deferred_billing) {
        settings = { ...settings, deferred_billing_enabled: false };
      }

      const printTicket = () => {
        if (!allowTicketPrint) return;
        printSaleTicket({
          sale,
          branchName,
          cashierName,
        });
      };

      const createPending = async (requestedImmediateIssue) => {
        try {
          await invoiceService.createPendingFromSale({
            sale_id: sale.id,
            requested_immediate_issue: requestedImmediateIssue,
          });
        } catch (error) {
          showSnack?.(
            extractErrorMessage(error, "No se pudo crear la pendiente de facturacion."),
            "error"
          );
        }
      };

      if (!settings.tickets_enabled && !settings.invoices_enabled) {
        showSnack?.("Configuracion FEL invalida: activa tickets o facturas.", "error");
        return;
      }

      if (settings.tickets_enabled && !settings.invoices_enabled) {
        printTicket();
        return;
      }

      if (settings.invoices_enabled && settings.deferred_billing_enabled) {
        await createPending(false);
        if (settings.tickets_enabled) {
          printTicket();
        }
        return;
      }

      const wantsInvoice = await askInvoicePrint();
      await createPending(Boolean(wantsInvoice));

      if (!wantsInvoice && settings.tickets_enabled) {
        printTicket();
      }
    },
    [askInvoicePrint, showSnack]
  );

  const invoiceDialog = useMemo(
    () => (
      <Dialog open={dialogOpen} onClose={() => resolveDialog(false)}>
        <DialogTitle>Facturación de venta</DialogTitle>
        <DialogContent>
          <Typography variant="body2">¿Deseas imprimir la factura?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resolveDialog(false)}>No</Button>
          <Button variant="contained" onClick={() => resolveDialog(true)}>
            Sí
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [dialogOpen, resolveDialog]
  );

  return {
    handlePostSaleInvoiceFlow,
    invoiceDialog,
  };
}
